import React, { useCallback, useState, useEffect, Fragment } from "react";
import makeStyles from "@mui/styles/makeStyles";
import { Box, Card, Switch } from "@mui/material";
import { useSelector } from "react-redux";
import { isUserAuthor } from "../auth/authUtils";
import { getOAuthLoginAsUrl } from "../auth/authEpic";
import {
  AUTHOR_PANEL_BASE_URL,
  AUTHOR_PANEL_CLIENT_ACCESS_KEY,
  CREATOR_BASE_URL,
  OPTIN_BASE_URL,
  OPTIN_CLIENT_ACCESS_KEY,
} from "../props";
import Grid from "@mui/material/Grid";
import Avatar from "@mui/material/Avatar";
import PersonIcon from "@mui/icons-material/Person";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import YesIcon from "@mui/icons-material/Check";
import {
  LOGIN_URL,
  useSendCreatorAccountEmail,
  useSendPasswordReset,
  useSendVerificationEmail,
  VERIFY_EMAIL_URL,
} from "./userServices";
import { isBlank, notBlank } from "../util/ext";
import ChangePasswordDialog from "./ChangePasswordDialog";
import { any, not } from "ramda";

const useStyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(3),
    margin: theme.spacing(1),
    marginTop: theme.spacing(3),
  },

  topBar: {
    display: "flex",
    justifyContent: "space-between",
    marginBottom: theme.spacing(3),
  },

  thumbnail: {
    width: 200,
    height: 200,
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
  },

  icon: {
    fontSize: "10rem",
  },
}));

const isCreatorUser = user =>
  any(
    org =>
      any(
        r =>
          r.roleName === "ROLE_CREATOR_USER" ||
          r.roleName === "ROLE_CREATOR_MANAGER",
        org.userOrganizationRoles,
      ),
    user.organizations,
  );

const UserDetailsCard = ({ user, onChange, onAlert }) => {
  const classes = useStyles();
  const auth = useSelector(state => state.auth);

  const [authorPanelAdminUrl, setAuthorPanelAdminUrl] = useState("");
  const [authorPanelAdminUrl_OLD, setAuthorPanelAdminUrl_OLD] = useState("");

  useEffect(() => {
    if (notBlank(auth.user.token)) {
      const url = getOAuthLoginAsUrl({
        adminToken: auth.user.token,
        username: user.username,
        // Author Panel builds the redirect URL with URL().href,
        // which leaves a trailing slash. We do the same here so the
        // redirect URLs match
        redirectUri: new URL(AUTHOR_PANEL_BASE_URL).href,
        clientId: AUTHOR_PANEL_CLIENT_ACCESS_KEY,
      });
      const url_OLD = getOAuthLoginAsUrl({
        adminToken: auth.user.token,
        username: user.username,
        redirectUri: `${OPTIN_BASE_URL}/author-panel`,
        clientId: OPTIN_CLIENT_ACCESS_KEY,
      });

      setAuthorPanelAdminUrl(url);
      setAuthorPanelAdminUrl_OLD(url_OLD);
    }
  }, [auth, user]);

  const handleTextChange = path => event => {
    const value = event.target.value;
    onChange(path, value);
  };

  const handleCheckboxChange = path => event => {
    const value = event.target.checked;
    onChange(path, value);
  };

  const [changePasswordDialogOpen, setChangePasswordDialogOpen] = useState(
    false,
  );

  const handlePasswordChangeSuccess = useCallback(() => {
    setChangePasswordDialogOpen(false);
    onAlert("success", ["Password successfully updated."]);
  }, [onAlert]);

  const resetPassword = useSendPasswordReset();
  const handleResetPasswordClicked = useCallback(async () => {
    if (notBlank(user.emailAddress) && user.emailAddressVerified) {
      try {
        await resetPassword.mutateAsync({
          resourceIds: {
            id: user.userId,
          },
          data: {
            emailAddress: user.emailAddress,
            targetUrl: LOGIN_URL,
            includeToken: true,
          },
        });
        onAlert("success", ["Password reset email sent."]);
      } catch (err) {
        onAlert("error", [err.message]);
      }
    }
  }, [resetPassword, user, onAlert]);

  const verificationEmail = useSendVerificationEmail();
  const handleSendVerificationEmailClicked = useCallback(async () => {
    if (notBlank(user.emailAddress)) {
      try {
        await verificationEmail.mutateAsync({
          resourceIds: {
            id: user.userId,
          },
          data: {
            emailAddress: user.emailAddress,
            targetUrl: VERIFY_EMAIL_URL,
          },
        });
        onAlert("success", ["Verification email sent."]);
      } catch (err) {
        onAlert("error", [err.message]);
      }
    }
  }, [verificationEmail, user, onAlert]);

  const creatorAccountEmail = useSendCreatorAccountEmail();
  const handleSendCreatorAccountEmailClicked = useCallback(async () => {
    if (notBlank(user.emailAddress)) {
      try {
        await creatorAccountEmail.mutateAsync({
          resourceIds: {
            id: user.userId,
          },
          data: {
            emailAddress: user.emailAddress,
            targetUrl: CREATOR_BASE_URL,
          },
        });
        onAlert("success", ["Creator Account email sent."]);
      } catch (err) {
        onAlert("error", [err.message]);
      }
    }
  }, [creatorAccountEmail, user, onAlert]);

  return (
    <Card classes={{ root: classes.root }}>
      <ChangePasswordDialog
        open={changePasswordDialogOpen}
        onCancel={useCallback(() => setChangePasswordDialogOpen(false), [])}
        onOk={handlePasswordChangeSuccess}
        user={user}
      />
      <Box className={classes.topBar}>
        <Box style={{ marginBottom: "10px" }}>
          <Typography variant="h4" noWrap>
            {user.userId}
          </Typography>
        </Box>

        <Grid container spacing={3} direction="row" justifyContent="flex-end">
          <Grid item>
            <Button
              variant="outlined"
              color="primary"
              onClick={() => setChangePasswordDialogOpen(true)}
            >
              Change Password
            </Button>
          </Grid>

          <Grid item>
            <Button
              variant="outlined"
              disabled={!user.emailAddressVerified}
              color="primary"
              onClick={handleResetPasswordClicked}
            >
              Send Password Reset
            </Button>
          </Grid>

          <Grid item>
            <Button
              variant="outlined"
              color="primary"
              disabled={isBlank(user.emailAddress)}
              onClick={handleSendVerificationEmailClicked}
            >
              Send Email Verification
            </Button>
          </Grid>

          <Grid item>
            <Button
              variant="outlined"
              color="primary"
              disabled={isBlank(user.emailAddress) || not(isCreatorUser(user))}
              onClick={handleSendCreatorAccountEmailClicked}
            >
              Send Creator Account Email
            </Button>
          </Grid>

          <Grid item>
            <Button
              variant="outlined"
              color="primary"
              disabled={true}
              // disabled={isBlank(user.emailAddress)}
            >
              Send Welcome Email
            </Button>
          </Grid>

          {isUserAuthor(user) && (
            <Fragment>
              <Grid item>
                <Button
                  variant="outlined"
                  color="primary"
                  disabled={isBlank(authorPanelAdminUrl_OLD)}
                  component="a"
                  target="_blank"
                  href={authorPanelAdminUrl_OLD}
                >
                  Log in to OLD Author Panel as Author
                </Button>
              </Grid>

              <Grid item>
                <Button
                  variant="outlined"
                  color="primary"
                  disabled={isBlank(authorPanelAdminUrl)}
                  component="a"
                  target="_blank"
                  href={authorPanelAdminUrl}
                >
                  Log in to Author Panel as Author
                </Button>
              </Grid>
            </Fragment>
          )}
        </Grid>
      </Box>
      <Grid
        container
        alignItems="flex-start"
        justifyContent="flex-start"
        direction="row"
        spacing={2}
      >
        <Grid item>
          <div className={classes.thumbnail}>
            <Avatar alt={user.username} className={classes.thumbnail}>
              <PersonIcon classes={{ root: classes.icon }} />
            </Avatar>
          </div>
        </Grid>

        <Grid item md={true}>
          <Box>
            <Grid container alignItems="center" spacing={2}>
              <Grid
                item
                md={user.privacyPolicyAccepted ? 1 : 12}
                component="label"
                container
                alignItems="center"
                spacing={1}
              >
                <Grid item>Inactive</Grid>
                <Grid item>
                  <Switch
                    checked={user.active}
                    color="primary"
                    onChange={handleCheckboxChange(["active"])}
                  />
                </Grid>
                <Grid item>Active</Grid>
              </Grid>

              {user.privacyPolicyAccepted && (
                <Grid item md={11}>
                  <Typography color="primary" variant={"subtitle1"}>
                    {`Privacy Policy Accepted on ${new Date(
                      user.privacyPolicyAcceptedDate,
                    ).toLocaleString()}`}
                  </Typography>
                </Grid>
              )}

              <Grid item md={7}>
                <TextField
                  label="Username"
                  fullWidth
                  value={user.username}
                  onChange={handleTextChange(["username"])}
                />
              </Grid>

              <Grid item md={7}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item md={true}>
                    <TextField
                      label="Email Address"
                      fullWidth
                      value={user.emailAddress || ""}
                      onChange={handleTextChange(["emailAddress"])}
                    />
                  </Grid>
                  {user.emailAddressVerified && (
                    <Grid item md={"auto"}>
                      <Box>
                        <YesIcon />
                        <Typography
                          component="span"
                          variant="subtitle1"
                          color="primary"
                          style={{ marginLeft: "5px" }}
                        >
                          Verified
                        </Typography>
                      </Box>
                    </Grid>
                  )}

                  {user.profile.welcomeEmailSent && (
                    <Grid item md={"auto"}>
                      <Box>
                        <YesIcon />
                        <Typography
                          component="span"
                          variant="subtitle1"
                          color="primary"
                          style={{ marginLeft: "5px" }}
                        >
                          Welcome Sent
                        </Typography>
                      </Box>
                    </Grid>
                  )}
                </Grid>
              </Grid>

              <Grid item md={6} container spacing={2}>
                <Grid item md={6}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={user.passwordResetAllowed}
                        onChange={handleCheckboxChange([
                          "passwordResetAllowed",
                        ])}
                        color="primary"
                      />
                    }
                    label="Password Reset Allowed"
                  />
                </Grid>

                <Grid item md={6}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={user.forcePasswordReset}
                        onChange={handleCheckboxChange(["forcePasswordReset"])}
                        color="primary"
                      />
                    }
                    label="Force Password Reset on Next Login"
                  />
                </Grid>
              </Grid>
            </Grid>
          </Box>
        </Grid>
      </Grid>
    </Card>
  );
};

export default UserDetailsCard;
